import { ApiGoalConfidenceLevel, ApiGoalMetricType } from "@/types/goals.types.ts";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

export const createKeyResultSchema = (t: TFunction) => {
  const requiredMessage = t("errors:field_required");

  return z
    .object({
      title: z.string().min(1, requiredMessage),
      description: z.string().nullable(),
      metric_type: z.enum(ApiGoalMetricType, { error: requiredMessage }),
      start_value: z
        .number()
        .nullable()
        .refine((value) => value !== null, { message: requiredMessage }),
      target_value: z
        .number()
        .nullable()
        .refine((value) => value !== null, { message: requiredMessage }),
      current_value: z.number().nullable(),
      currency: z.string().nullable(),
      confidence_level: z.enum(ApiGoalConfidenceLevel, { error: requiredMessage }),
      weight: z
        .number({ error: requiredMessage })
        .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
        .max(999.99, t("api_errors:maximum_value_must", { maximum: 999.99 }))
        .refine((value) => /^\d{1,3}(\.\d{1,2})?$/.test(value.toString()), {
          message: t("errors:invalid_format"),
        }),
      weight_mode: z.string().min(1, requiredMessage),
    })
    .superRefine((data, context) => {
      if (data.metric_type === ApiGoalMetricType.currency && !data.currency) {
        context.addIssue({ code: "custom", path: ["currency"], message: requiredMessage });
      }
    });
};

export type KeyResultFormValues = z.input<ReturnType<typeof createKeyResultSchema>>;
export type KeyResultSchema = z.output<ReturnType<typeof createKeyResultSchema>>;

export interface ValueFieldsProps {
  formHook: UseFormReturn<KeyResultFormValues, unknown, KeyResultSchema>;
  isCreating?: boolean;
}
